"use client";

import Link from "next/link";
import type { LucideIcon } from "lucide-react";
import {
  ActivitySquare,
  AlertTriangle,
  ChevronsLeft,
  ChevronsRight,
  FileText,
  LayoutDashboard,
  ListChecks,
  LogOut,
  Mail,
  MapPin,
  PlusCircle,
  ReceiptText,
  Server,
  Settings,
  ShieldAlert,
  ShoppingCart,
  Sprout,
  Store,
  UserRound,
  Users,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { SignOutConfirmDialog } from "@/components/blocks/SignOutConfirmDialog";
import { cn } from "@/lib/utils";
import type { UserAccount, UserRole } from "@/features/users/user.type";
import { useCartTotalQuantity } from "@/features/shop/cart/cart.store";
import { getRoleRoute } from "../role-paths";
import { SidebarNavItem } from "./sidebar-nav-item";

interface RoleNavItem {
  key: string;
  href: string;
  icon: LucideIcon;
  label: string;
  matchMode: "exact" | "prefix";
  badge?: number;
}

/** Single source of truth for the sidebar: one flat, ordered nav-item list per role. */
function getRoleNavItems(
  role: UserRole | null | undefined,
  cartCount: number,
): RoleNavItem[] {
  const homeHref = getRoleRoute(role, "home");
  const casesHref = getRoleRoute(role, "cases");
  const farmsHref = getRoleRoute(role, "farms");
  const shopHref = getRoleRoute(role, "shop");
  const farmersHref = getRoleRoute(role, "farmers");
  const reportHref = getRoleRoute(role, "report");
  const profileHref = getRoleRoute(role, "profile");

  switch (role) {
    case "admin":
      return [
        { key: "home", href: homeHref, icon: ShieldAlert, label: "Admin Center", matchMode: "exact" },
        { key: "farmers", href: farmersHref, icon: Users, label: "Farmers", matchMode: "exact" },
        { key: "users", href: "/admin/users", icon: Users, label: "Users", matchMode: "prefix" },
        { key: "officers", href: "/admin/officers", icon: ShieldAlert, label: "Officers", matchMode: "prefix" },
        { key: "locations", href: "/admin/locations", icon: MapPin, label: "Locations", matchMode: "prefix" },
        { key: "newsletter", href: "/admin/newsletter", icon: Mail, label: "Newsletter", matchMode: "prefix" },
        { key: "pipeline", href: "/admin/pipeline", icon: ListChecks, label: "AI Pipeline", matchMode: "exact" },
        { key: "queues", href: "/admin/queues", icon: Server, label: "Background Jobs", matchMode: "exact" },
        { key: "audit", href: "/admin/audit", icon: ActivitySquare, label: "Audit Log", matchMode: "exact" },
        { key: "error-logs", href: "/admin/error-logs", icon: AlertTriangle, label: "Error Logs", matchMode: "exact" },
        { key: "settings", href: "/admin/settings", icon: Settings, label: "Settings", matchMode: "exact" },
        { key: "profile", href: "/admin/profile", icon: UserRound, label: "Profile", matchMode: "exact" },
      ];

    case "farmer":
      return [
        { key: "home", href: homeHref, icon: LayoutDashboard, label: "Overview", matchMode: "exact" },
        { key: "farms", href: farmsHref, icon: Sprout, label: "Farms", matchMode: "prefix" },
        { key: "report", href: reportHref, icon: PlusCircle, label: "Report Issue", matchMode: "exact" },
        { key: "shop", href: shopHref, icon: Store, label: "My Shops", matchMode: "prefix" },
        { key: "cases", href: casesHref, icon: FileText, label: "My Cases", matchMode: "prefix" },
        { key: "profile", href: profileHref, icon: UserRound, label: "Profile", matchMode: "exact" },
      ];

    case "extension_officer":
    case "veterinary_officer":
    case "county_supervisor":
      return [
        { key: "home", href: homeHref, icon: LayoutDashboard, label: "Overview", matchMode: "exact" },
        { key: "cases", href: casesHref, icon: FileText, label: "Cases", matchMode: "prefix" },
        { key: "farmers", href: farmersHref, icon: Users, label: "Farmers", matchMode: "exact" },
        { key: "profile", href: profileHref, icon: UserRound, label: "Profile", matchMode: "exact" },
      ];

    case "customer":
      return [
        { key: "home", href: homeHref, icon: LayoutDashboard, label: "Shop", matchMode: "exact" },
        { key: "cart", href: "/shop/cart", icon: ShoppingCart, label: "Cart", matchMode: "prefix", badge: cartCount },
        { key: "orders", href: "/shop/orders", icon: ReceiptText, label: "Orders", matchMode: "prefix" },
      ];

    default:
      return [
        { key: "home", href: homeHref, icon: LayoutDashboard, label: "Overview", matchMode: "exact" },
        { key: "cases", href: casesHref, icon: FileText, label: "Cases", matchMode: "prefix" },
        { key: "profile", href: profileHref, icon: UserRound, label: "Profile", matchMode: "exact" },
      ];
  }
}

interface AppSidebarProps {
  user: UserAccount | null | undefined;
  isOpen: boolean;
  isCollapsed: boolean;
  isShopRoute: boolean;
  onCollapseToggle: () => void;
  onNavigate: () => void;
  onSignOut: () => void;
}

export function AppSidebar({
  user,
  isOpen,
  isCollapsed,
  isShopRoute,
  onCollapseToggle,
  onNavigate,
  onSignOut,
}: AppSidebarProps) {
  const sidebarWidthClass = isCollapsed ? "md:w-16" : "md:w-64";

  return (
    <aside
      className={cn(
        "fixed inset-y-0 left-0 z-50 w-64 shrink-0 bg-card border-r border-white/10 transition-transform duration-300 ease-in-out transform flex flex-col shadow-xs md:relative md:inset-auto md:h-full md:translate-x-0 md:bg-card/60 md:backdrop-blur-2xl md:transition-[width] md:duration-300 md:ease-out",
        isShopRoute && "md:hidden",
        sidebarWidthClass,
        isOpen ? "translate-x-0" : "-translate-x-full",
      )}
    >
      <div className="flex h-full min-h-0 flex-col p-4 md:p-3">
        <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto">
          {isShopRoute ? (
            <ShopAccountNav
              user={user}
              profileHref={getRoleRoute(user?.role, "profile")}
              onSignOut={onSignOut}
            />
          ) : (
            <RoleNavigation
              user={user}
              isCollapsed={isCollapsed}
              onCollapseToggle={onCollapseToggle}
              onNavigate={onNavigate}
            />
          )}
        </div>

        {!isShopRoute && user ? (
          <SidebarUserPanel
            user={user}
            isCollapsed={isCollapsed}
            onSignOut={onSignOut}
          />
        ) : null}
      </div>
    </aside>
  );
}

interface ShopAccountNavProps {
  user: UserAccount | null | undefined;
  profileHref: string;
  onSignOut: () => void;
}

function ShopAccountNav({ user, profileHref, onSignOut }: ShopAccountNavProps) {
  const cartCount = useCartTotalQuantity();

  return (
    <div className="space-y-3">
      <div className="mb-2 flex items-center justify-between px-3">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Account
        </h4>
      </div>

      {user ? (
        <>
          <Button
            asChild
            variant="outline"
            className="w-full justify-start gap-2 bg-transparent"
          >
            <Link href={profileHref}>
              <UserRound className="h-4 w-4" />
              Account
            </Link>
          </Button>
          <Button asChild className="w-full justify-between gap-2">
            <Link href="/shop/cart">
              <span className="flex items-center gap-2">
                <ShoppingCart className="h-4 w-4" />
                Cart
              </span>
              {cartCount > 0 ? (
                <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-primary-foreground/20 px-1 text-[10px] font-bold leading-none">
                  {cartCount > 99 ? "99+" : cartCount}
                </span>
              ) : null}
            </Link>
          </Button>
          <SignOutConfirmDialog onConfirm={onSignOut}>
            <Button
              variant="outline"
              className="w-full justify-start gap-2 bg-transparent hover:border-destructive/30 hover:bg-destructive/10 hover:text-destructive"
            >
              <LogOut className="h-4 w-4" />
              Sign Out
            </Button>
          </SignOutConfirmDialog>
        </>
      ) : (
        <Button asChild className="w-full">
          <Link href="/login">Login</Link>
        </Button>
      )}
    </div>
  );
}

interface RoleNavigationProps {
  user: UserAccount | null | undefined;
  isCollapsed: boolean;
  onCollapseToggle: () => void;
  onNavigate: () => void;
}

function RoleNavigation({
  user,
  isCollapsed,
  onCollapseToggle,
  onNavigate,
}: RoleNavigationProps) {
  const cartCount = useCartTotalQuantity();
  const items = getRoleNavItems(user?.role, cartCount);

  return (
    <div className="space-y-1">
      <div className="mb-2 flex items-center justify-end px-3">
        <Button
          variant="ghost"
          size="icon"
          className="hidden md:inline-flex"
          onClick={onCollapseToggle}
          aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {isCollapsed ? (
            <ChevronsRight className="h-4 w-4" />
          ) : (
            <ChevronsLeft className="h-4 w-4" />
          )}
        </Button>
      </div>

      {items.map((item) => (
        <SidebarNavItem
          key={item.key}
          onNavigate={onNavigate}
          href={item.href}
          icon={item.icon}
          label={item.label}
          matchMode={item.matchMode}
          collapsed={isCollapsed}
          badge={item.badge}
        />
      ))}
    </div>
  );
}

interface SidebarUserPanelProps {
  user: UserAccount;
  isCollapsed: boolean;
  onSignOut: () => void;
}

function SidebarUserPanel({
  user,
  isCollapsed,
  onSignOut,
}: SidebarUserPanelProps) {
  const initials =
    user.fullName
      ?.split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part.charAt(0).toUpperCase())
      .join("") || "U";

  return (
    <div className="mt-auto border-t border-white/10 pt-6">
      <div className="mb-2 flex items-center gap-3 rounded bg-secondary/30 p-2 px-3 backdrop-blur-sm transition-all duration-300 ease-out hover:bg-secondary/40">
        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/15 text-xs font-semibold text-primary">
          {initials}
        </div>
        <div className={cn("min-w-0 flex-1", isCollapsed && "md:hidden")}>
          <p className="truncate text-sm font-medium leading-none">
            {user.fullName}
          </p>
          <p className="mt-1 truncate text-xs capitalize text-muted-foreground">
            {user.role?.replaceAll("_", " ").toLowerCase()}
          </p>
        </div>
      </div>

      <SignOutConfirmDialog onConfirm={onSignOut}>
        <Button
          variant="outline"
          className={cn(
            "w-full justify-center gap-2 bg-transparent transition-all duration-300 ease-out hover:-translate-y-px hover:border-destructive/30 hover:bg-destructive/10 hover:text-destructive",
            isCollapsed && "md:px-0",
          )}
          title="Sign Out"
        >
          <LogOut className="h-4 w-4" />
          <span className={cn(isCollapsed && "md:hidden")}>Sign Out</span>
        </Button>
      </SignOutConfirmDialog>
    </div>
  );
}
